import { AgenticPlatformAskUserFieldSchema } from 'agentic-platform-schema';
import { z } from 'zod';

import { EventAction } from '../types';

export const AgenticChatSurfaceSchema = z.enum([
  'RecentChatSuggestions',
  'NewChatScreen',
  'SessionHistoryLink',
  'ChatLauncher',
  'LauncherRecentChats',
  'PanelClose',
  'PanelNewChat',
  'PanelRecentChats',
]);
export type AgenticChatSurface = z.infer<typeof AgenticChatSurfaceSchema>;

export const AgenticChatPromptIdSchema = z.enum([
  'CheckFlowReadiness',
  'DocumentFlow',
  'InvestigateFailedRuns',
  'ClarifyBuildRequirements',
]);
export type AgenticChatPromptId = z.infer<typeof AgenticChatPromptIdSchema>;

export const ContentFreeAgenticChatPropertySchemasByAction = {
  [EventAction.PanelOpen]: z.strictObject({
    surface: AgenticChatSurfaceSchema,
    sessionId: z.uuidv7().optional(),
  }),
  [EventAction.PanelClose]: z.strictObject({
    surface: AgenticChatSurfaceSchema,
    openedVia: AgenticChatSurfaceSchema,
    sessionId: z.uuidv7().optional(),
  }),
  [EventAction.RecentChatsMenuOpen]: z.strictObject({ surface: AgenticChatSurfaceSchema }),
  [EventAction.RecentChatSelect]: z.strictObject({
    surface: AgenticChatSurfaceSchema,
    sessionId: z.uuidv7(),
    listPosition: z.number().int().min(1),
    listLength: z.number().int().min(1),
    isCurrentChat: z.boolean(),
  }),
  [EventAction.ChatCreate]: z.strictObject({
    surface: AgenticChatSurfaceSchema,
    sessionId: z.uuidv7(),
  }),
  [EventAction.MessageSend]: z.strictObject({
    sessionId: z.uuidv7(),
    /** MUI x-chat-headless's createLocalId uses local-<milliseconds>-<counter> without crypto.randomUUID. */
    messageId: z.union([z.uuid(), z.string().regex(/^local-\d{13}-[1-9]\d*$/)]),
    openedVia: AgenticChatSurfaceSchema.optional(),
    source: z.enum(['Composer', 'QuestionAnswer', 'QuestionSkip', 'SuggestedPrompt']),
    promptId: AgenticChatPromptIdSchema.optional(),
    followsQuestion: z.boolean(),
    questionFieldCount: z.number().int().min(1).max(5).optional(),
    questionFieldKinds: z
      .array(
        z.enum(AgenticPlatformAskUserFieldSchema.options.map((field) => field.shape.kind.value))
      )
      .max(4)
      .optional(),
  }),
  [EventAction.ResponseStop]: z.strictObject({
    sessionId: z.uuidv7().optional(),
    trigger: z.enum(['StopButton', 'EscapeKey']),
  }),
} satisfies Partial<Record<EventAction, z.ZodObject>>;

export type ContentFreeAgenticChatEvent = {
  [Action in keyof typeof ContentFreeAgenticChatPropertySchemasByAction]: {
    action: Action;
    properties: z.infer<(typeof ContentFreeAgenticChatPropertySchemasByAction)[Action]>;
  };
}[keyof typeof ContentFreeAgenticChatPropertySchemasByAction];
